import React from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGrowthPillars } from "@/hooks/useGrowthPillars";
import { useGrowthMetrics } from "@/hooks/useGrowthMetrics";
import { useMetricLookup } from "@/hooks/useMetricLookup";

interface GrowthMetricsSelectProps {
  salesChannelId: string;
  selectedMetric: string;
  onMetricChange: (metric: string) => void;
  label?: string;
}

const GrowthMetricsSelect = ({ 
  salesChannelId, 
  selectedMetric, 
  onMetricChange,
  label = "Affecting Metric"
}: GrowthMetricsSelectProps) => {
  const { growthPillars } = useGrowthPillars(salesChannelId);
  const { getMetricName } = useMetricLookup(salesChannelId);

  // Get display name for selected metric (handles both UUID and legacy text)
  const selectedDisplayName = selectedMetric ? getMetricName(selectedMetric) : "";

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={selectedMetric} onValueChange={onMetricChange}>
        <SelectTrigger>
          <SelectValue placeholder="Select a growth metric">
            {selectedDisplayName || "Select a growth metric"}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-[300px] overflow-y-auto">
          {growthPillars.map((pillar) => (
            <PillarMetrics
              key={pillar.id}
              pillar={pillar}
            />
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// Helper component to render metrics for each pillar
const PillarMetrics = ({ 
  pillar 
}: { 
  pillar: any; 
}) => {
  const { growthMetrics } = useGrowthMetrics(pillar.id);

  if (growthMetrics.length === 0) return null;

  // Sort metrics: North Star first, then by name
  const sortedMetrics = [...growthMetrics].sort((a, b) => {
    if (a.category === 'North Star' && b.category !== 'North Star') return -1;
    if (a.category !== 'North Star' && b.category === 'North Star') return 1;
    return a.name.localeCompare(b.name);
  });

  return (
    <>
      {sortedMetrics.map((metric) => (
        <SelectItem key={metric.id} value={metric.id}>
          <div className="flex flex-col">
            <span className="font-medium">{metric.name}</span>
            <span className="text-xs text-muted-foreground">
              {pillar.name} • {metric.category}
            </span>
          </div>
        </SelectItem>
      ))}
    </>
  );
};

export default GrowthMetricsSelect;